import {
  BodyVariants, HeaderVariants, LabelVariants, SubtitleVariants, UtilityVariants, VariantSection,
} from './types';

const families = {
  header: 'Rubik, "Helvetica Neue", Arial, "sans-serif"',
  body: 'Roboto, "Helvetica Neue", Arial, "sans-serif"',
  mono: '"Roboto Mono", monospace',
};

export const Header: VariantSection<HeaderVariants> = {
  heading: 'Header',
  text: 'In the type scale, headlines span from a range of 1 through 6. Headlines are the largest text on the screen, reserved for short, important text or numerals.',
  variants: {
    h1: {
      name: 'h1',
      tag: 'h1',
      styles: {
        fontFamily: families.header,
        fontSize: '3.5rem', // 56px
        lineHeight: '1.1429em', // 64px
        fontWeight: 400,
      },
    },
    h2: {
      name: 'h2',
      tag: 'h2',
      styles: {
        fontFamily: families.header,
        fontSize: '2.75rem', // 44px
        lineHeight: '1.1818em', // 52px
        fontWeight: 400,
      },
    },
    h3: {
      name: 'h3',
      tag: 'h3',
      styles: {
        fontFamily: families.header,
        fontSize: '2.25rem', // 36px
        lineHeight: '1.2222em', // 44px
        fontWeight: 400,
      },
    },
    h4: {
      name: 'h4',
      tag: 'h4',
      styles: {
        fontFamily: families.header,
        fontSize: '2rem', // 32px
        lineHeight: '1.25em', // 40px
        fontWeight: 400,
      },
    },
    h5: {
      name: 'h5',
      tag: 'h5',
      styles: {
        fontFamily: families.header,
        fontSize: '1.75rem', // 28px
        lineHeight: '1.2857em', // 36px
        fontWeight: 500,
      },
    },
    h6: {
      name: 'h6',
      tag: 'h6',
      styles: {
        fontFamily: families.header,
        fontSize: '1.5rem', // 24px
        lineHeight: '1.3333em', // 32px
        fontWeight: 500,
      },
    },
  },
};

export const Subtitle: VariantSection<SubtitleVariants> = {
  heading: 'Subtitle',
  text: 'Subtitles are smaller than headlines. They are typically reserved for medium-emphasis text that is shorter in length.',
  variants: {
    subtitle1: {
      name: 'subtitle1',
      tag: 'h6',
      styles: {
        fontFamily: families.header,
        fontSize: '1rem', // 16px
        lineHeight: '1.5em', // 24px
        fontWeight: 500,
        letterSpacing: '0.0094em', // 0.15px
      },
    },
    subtitle2: {
      name: 'subtitle2',
      tag: 'h6',
      styles: {
        fontFamily: families.header,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.4286em', // 20px
        fontWeight: 500,
        letterSpacing: '0.0179em', // 0.25px
      },
    },
  },
};

export const Body: VariantSection<BodyVariants> = {
  heading: 'Body',
  text: 'Body text comes in ranges 1-3, scaled from largest (same as headers), and it’s typically used for long-form writing as it works well for small text sizes.',
  variants: {
    body1: {
      name: 'body1',
      tag: 'p',
      allowStrong: true,
      styles: {
        fontFamily: families.body,
        fontSize: '1rem', // 16px
        lineHeight: '1.5em', // 24px
        fontWeight: 400,
        letterSpacing: '0.0094em', // 0.15px
      },
    },
    body2: {
      name: 'body2',
      tag: 'p',
      allowStrong: true,
      styles: {
        fontFamily: families.body,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.4286em', // 20px
        fontWeight: 400,
        letterSpacing: '0.0179em', // 0.25px
      },
    },
    body3: {
      name: 'body3',
      tag: 'p',
      allowStrong: true,
      styles: {
        fontFamily: families.body,
        fontSize: '0.75rem', // 12px
        lineHeight: '1.3333em', // 16px
        fontWeight: 400,
        letterSpacing: '0.0333em', // 0.4px
      },
    },
  },
};

export const Label: VariantSection<LabelVariants> = {
  heading: 'Label',
  text: 'Label styles are smaller, utilitarian styles, used for things like the text inside components or for very small text in the content body, such as captions. Buttons, for example, use the label large style.',
  variants: {
    label1: {
      name: 'label1',
      tag: 'span',
      styles: {
        fontFamily: families.header,
        fontSize: '1rem', // 16px
        lineHeight: '1.25em', // 20px
        fontWeight: 500,
        letterSpacing: '0.0063em', // 0.1px
      },
    },
    label2: {
      name: 'label2',
      tag: 'span',
      styles: {
        fontFamily: families.header,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.4286em', // 20px
        fontWeight: 500,
        letterSpacing: '0.0071em', // 0.1px
      },
    },
    label3: {
      name: 'label3',
      tag: 'span',
      styles: {
        fontFamily: families.header,
        fontSize: '0.75rem', // 12px
        lineHeight: '1.3333em', // 16px
        fontWeight: 500,
        letterSpacing: '0.0417em', // 0.5px
      },
    },
  },
};

export const Utility: VariantSection<UtilityVariants> = {
  heading: 'Utility',
  text: '',
  variants: {
    link1: {
      name: 'link1',
      tag: 'span',
      styles: {
        fontFamily: families.body,
        fontSize: '1rem', // 16px
        lineHeight: '1.5em', // 24px
        fontWeight: 400,
        letterSpacing: '0.0094em', // 0.15px
        textDecoration: 'underline',
      },
    },
    link2: {
      name: 'link2',
      tag: 'span',
      styles: {
        fontFamily: families.body,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.4286em', // 20px
        fontWeight: 400,
        letterSpacing: '0.0179em', // 0.25px
        textDecoration: 'underline',
      },
    },
    link3: {
      name: 'link3',
      tag: 'span',
      styles: {
        fontFamily: families.body,
        fontSize: '0.75rem', // 12px
        lineHeight: '1.3333em', // 16px
        fontWeight: 400,
        letterSpacing: '0.0333em', // 0.4px
        textDecoration: 'underline',
      },
    },
    mono1: {
      name: 'mono1',
      tag: 'span',
      allowStrong: true,
      styles: {
        fontFamily: families.mono,
        fontSize: '1rem', // 16px
        lineHeight: '1.5em', // 24px
        fontWeight: 400,
      },
    },
    mono2: {
      name: 'mono2',
      tag: 'span',
      allowStrong: true,
      styles: {
        fontFamily: families.mono,
        fontSize: '0.875rem', // 14px
        lineHeight: '1.4286em', // 20px
        fontWeight: 400,
      },
    },
    mono3: {
      name: 'mono3',
      tag: 'span',
      allowStrong: true,
      styles: {
        fontFamily: families.mono,
        fontSize: '0.75rem', // 12px
        lineHeight: '1.3333em', // 16px
        fontWeight: 400,
      },
    },
  },
};

export default [
  Header,
  Subtitle,
  Body,
  Label,
  Utility,
];
